import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
import { CATEGORY_LABELS, SITE_ORIGIN } from "@/lib/site";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import ChatWidget from "@/components/ChatWidget";
import Home from "@/pages/Home";
import VehicleTuning from "@/pages/VehicleTuning";
import MakeTuning from "@/pages/MakeTuning";
import TuningFiles from "@/pages/TuningFiles";
import Tools from "@/pages/Tools";
import ShopIndex, { ShopAll, ShopBrand } from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Dealer from "@/pages/Dealer";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminEdit from "@/pages/admin/AdminEdit";
import AdminFlex from "@/pages/admin/AdminFlex";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminKess3 from "@/pages/admin/AdminKess3";
import Kess3Builder from "@/pages/Kess3Builder";
import Autotuner from "@/pages/Autotuner";
import MagicFlex from "@/pages/MagicFlex";
import FlexVehicles from "@/pages/FlexVehicles";
import Kess3 from "@/pages/Kess3";
import FlexBuilder from "@/pages/FlexBuilder";
import TuningDatabase from "@/pages/TuningDatabase";
import TuningCategoryIndex from "@/pages/TuningCategoryIndex";
import TuningCategoryMake from "@/pages/TuningCategoryMake";
import TuningCategoryModel from "@/pages/TuningCategoryModel";
import TuningCategoryVehicle from "@/pages/TuningCategoryVehicle";
import AdminTuningData from "@/pages/admin/AdminTuningData";
import SignIn from "@/pages/SignIn";
import Account from "@/pages/Account";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import NotFound from "@/pages/NotFound";

const TITLES = {
  "/": "Regal Tuning | Professional ECU Tuning Files & Tuning Tools",
  "/tuning-files": "ECU Tuning Files | Regal Tuning",
  "/tuning-tools": "Professional Tuning Tools — Alientech, AutoTuner, FLEX, Dimsport | Regal Tuning",
  "/become-a-dealer": "Become an ECU Tuning Dealer | Regal Tuning",
  "/about": "About Regal Tuning — 20+ Years of ECU Calibration",
  "/contact": "Contact | Regal Tuning",
  "/autotuner": "AutoTuner ECU Tuning Tools | Master & Slave | Regal Tuning",
  "/autotuner-master": "AutoTuner Master | Regal Tuning",
  "/magicmotorsport-flex": "Magicmotorsport FLEX ECU & TCU Programmer | Regal Tuning",
  "/flex-vehicle-list": "FLEX Vehicle Compatibility List | Regal Tuning",
  "/alientech-kess3": "Alientech KESS3 Master & Slave Tools UK | Regal Tuning",
  "/tuning-database": "Vehicle Tuning Gains Database | Stage 1 Remap Figures | Regal Tuning",
  "/legal/privacy-policy": "Privacy Policy | Regal Tuning",
  "/legal/terms-and-conditions": "Terms & Conditions | Regal Tuning",
  "/legal/cookie-policy": "Cookie Policy | Regal Tuning",
  "/legal/returns-policy": "Returns Policy | Regal Tuning",
  "/legal/delivery-information": "Delivery Information | Regal Tuning",
};

const DESCRIPTIONS = {
  "/": "Professional ECU tuning files, genuine tuning tools (Alientech KESS3, AutoTuner, Magic Motorsport FLEX, Dimsport) and technical support for tuning businesses worldwide. 20+ years of ECU calibration experience.",
  "/tuning-files": "Professional ECU remapping files for cars, vans, HGV, agricultural, marine and motorcycles. Performance, economy and gearbox/TCU calibrations with fast turnaround via the Regal Tuning file portal.",
  "/tuning-tools": "Buy genuine professional ECU tuning tools — Alientech KESS3, AutoTuner, Magic Motorsport FLEX and Dimsport Master and Slave tools, accessories and subscriptions, with worldwide support.",
  "/become-a-dealer": "Start or grow your tuning business with Regal Tuning — professional tools, ECU tuning files, technical support, training and an ongoing worldwide file service for dealers.",
  "/about": "Regal Tuning — over 20 years of professional ECU calibration, from chip tuning to modern OBD/Bench/Boot tuning, with 4WD dyno development and a worldwide dealer network.",
  "/contact": "Contact Regal Tuning — tuning tool sales, dealer enquiries and technical support. Telephone 0333 772 1223 or email, with the customer file portal for ECU file uploads.",
  "/autotuner": "Discover genuine AutoTuner ECU and TCU tuning tools from Regal Tuning. Compare AutoTuner Master and Slave tools for OBD, Bench and Boot programming with professional tuning file support.",
  "/magicmotorsport-flex": "Buy Magicmotorsport FLEX Master and Slave tools from Regal Tuning. Professional ECU & TCU programming with OBD, Bench & Boot, UK support and FLEX Slave options linked to Regal Tuning's tuning file service.",
  "/flex-vehicle-list": "Check Magicmotorsport FLEX ECU & TCU compatibility by vehicle, engine, ECU, connection mode and software package — the official FLEX vehicle database, searchable on Regal Tuning.",
  "/alientech-kess3": "Buy Alientech KESS3 Master and Slave tools in the UK from Regal Tuning. OBD, Bench & Boot in one device — KESS3 Slave packages linked to Regal Tuning from £1,100, plus Build Your KESS3 configurators.",
  "/tuning-database": "Find your vehicle in the Regal Tuning database — compare original factory BHP and torque with verified Stage 1 tuned figures for cars, vans, trucks, agricultural, motorcycles and marine, then request your remap quote.",
  "/tuning-tools/alientech": "Shop genuine Alientech KESS3 tools, activations and Slave packages at Regal Tuning — ready-made KESS3 Slave packages linked to Regal Tuning, plus Master and Slave configurators.",
  "/tuning-tools/magic-motorsport": "Shop genuine Magic Motorsport FLEX Master and Slave tools at Regal Tuning — OBD, Bench & Boot packages, protocol activations and FLEX Slave tools linked to Regal Tuning.",
  "/legal/privacy-policy": "How Regal Tuning collects, uses and protects your personal data across the website, shop and contact forms — and how to request access, correction or deletion.",
  "/legal/terms-and-conditions": "Regal Tuning terms & conditions — supply of professional tuning tools, ECU calibration file service terms, liability and governing law.",
  "/legal/cookie-policy": "The small number of cookies and browser storage Regal Tuning uses to operate the shopping cart and remember preferences — no advertising trackers.",
  "/legal/returns-policy": "Regal Tuning returns policy — 14-day returns on unused hardware in original packaging; activated software, protocol packages and subscriptions are non-returnable once activated.",
  "/legal/delivery-information": "Regal Tuning delivery information — stocked hardware dispatched in 1–2 working days with tracked, insured worldwide couriers; digital products activated electronically the same working day.",
};

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const catMatch = pathname.match(/^\/tuning-tools\/([a-z-]+)$/);
    const CATEGORY_TITLES = { alientech: "Alientech KESS3 Tools | Master, Slave & Activations | Regal Tuning" };
    const catTitle = catMatch ? (CATEGORY_TITLES[catMatch[1]] || (CATEGORY_LABELS[catMatch[1]] ? `${CATEGORY_LABELS[catMatch[1]]} Tuning Tools | Regal Tuning` : null)) : null;
    document.title = TITLES[pathname] || catTitle || (pathname.startsWith("/tuning-tools") ? "Tuning Tools | Regal Tuning" : "Regal Tuning");
    const desc = DESCRIPTIONS[pathname];
    if (desc) document.querySelector('meta[name="description"]')?.setAttribute("content", desc);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = SITE_ORIGIN + (pathname === "/tuning-tools/magic-motorsport" ? "/magicmotorsport-flex" : pathname === "/tuning-tools/autotuner" ? "/autotuner" : pathname.startsWith("/tuning-database/trucks") || pathname.startsWith("/tuning-database/agriculture") ? pathname : pathname.startsWith("/tuning-database") ? "/tuning-database" : pathname);
  }, [pathname]);
  return null;
}

/** Client-side fallback for legacy Wix URLs. The genuine 301s are emitted
 *  server-side by serve.js (staging) and requested at the production edge via
 *  Emergent support; until the edge serves them, this guarantees visitors and
 *  crawlers landing on an old URL are taken straight to the matching new page
 *  (no homepage dumping, query string preserved). Mirrors serve.js exactly. */
const LEGACY_PAGE_REDIRECTS = {
  "/webshop": "/tuning-tools",
  "/about-1": "/about",
  "/copy-of-shop": "/tuning-tools/alientech",
};
const LEGACY_PRODUCT_SLUGS = new Set(["autotuner-master","autotuner-slave","autotuner-slave-any-master","bench-power-supply","ecu-pinout-kit","flex-master","flex-master-full","flex-master-obd-bench","flex-protocol-package","flex-slave","flex-slave-full-any","flex-slave-full-regal","flex-slave-obd-bench-any","flex-slave-obd-bench-regal","gpt-adapter-set","kess3-slave-regal-cars-obd","kess3-slave-regal-cars-obd-bench","kess3-slave-regal-full","new-genius-master","new-genius-slave","new-trasdata-master","obd-cable-set","race-evo-full","sub-autotuner-12m","sub-flex-12m","sub-kess3-12m","used-autotuner-slave","used-kess-v2-master"]);

function LegacyRedirectGate({ children }) {
  const { pathname, search } = useLocation();
  const p = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  let dest = LEGACY_PAGE_REDIRECTS[p];
  if (!dest && p.startsWith("/product-page/")) {
    const slug = p.slice("/product-page/".length).split("/")[0];
    dest = LEGACY_PRODUCT_SLUGS.has(slug) ? `/product/${slug}` : "/tuning-tools";
  }
  if (dest) return <Navigate to={dest + (search || "")} replace />;
  return children;
}

/**
 * Emergent-managed Google sign-in return gate. Must read the fragment from
 * useLocation().hash synchronously during render (NOT window.location.hash,
 * NOT a useEffect) so the one-time session_id is processed before any route
 * or auth check runs.
 */
function OAuthReturnGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <CartProvider>
          <ScrollManager />
          <OAuthReturnGate>
          <LegacyRedirectGate>
          <Header />
          <CartDrawer />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/tuning-files" element={<TuningFiles />} />
            <Route path="/tuning-tools" element={<Tools />} />
            <Route path="/tuning-tools/:category" element={<Tools />} />
            <Route path="/shop" element={<ShopIndex />} />
            <Route path="/shop/all" element={<ShopAll />} />
            <Route path="/shop/:brand" element={<ShopBrand />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/autotuner" element={<Autotuner />} />
            <Route path="/magicmotorsport-flex" element={<MagicFlex />} />
            <Route path="/flex-vehicle-list" element={<FlexVehicles />} />
            <Route path="/alientech-kess3" element={<Kess3 />} />
            <Route path="/tuning-database" element={<TuningDatabase />} />
            <Route path="/tuning-database/trucks" element={<TuningCategoryIndex category="trucks" />} />
            <Route path="/tuning-database/trucks/:make" element={<TuningCategoryMake category="trucks" />} />
            <Route path="/tuning-database/trucks/:make/:model" element={<TuningCategoryModel category="trucks" />} />
            <Route path="/tuning-database/trucks/:make/:model/:generation/:engine" element={<TuningCategoryVehicle category="trucks" />} />
            <Route path="/tuning-database/agriculture" element={<TuningCategoryIndex category="agriculture" />} />
            <Route path="/tuning-database/agriculture/:make" element={<TuningCategoryMake category="agriculture" />} />
            <Route path="/tuning-database/agriculture/:make/:model" element={<TuningCategoryModel category="agriculture" />} />
            <Route path="/tuning-database/agriculture/:make/:model/:generation/:engine" element={<TuningCategoryVehicle category="agriculture" />} />
            <Route path="/tuning-database/:make/:model/:engine" element={<TuningDatabase />} />
            <Route path="/tuning/:make" element={<MakeTuning />} />
            <Route path="/tuning/:make/:model/:generation/:engine" element={<VehicleTuning />} />
            <Route path="/autotuner-master" element={<ProductDetail forcedSlug="autotuner-master" />} />
            <Route path="/become-a-dealer" element={<Dealer />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal/:page" element={<Legal />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminProducts />} />
            <Route path="/admin/new" element={<AdminEdit />} />
            <Route path="/admin/edit/:id" element={<AdminEdit />} />
            <Route path="/admin/flex" element={<AdminFlex />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/build-your-flex/:type" element={<FlexBuilder />} />
            <Route path="/build-your-kess3/:type" element={<Kess3Builder />} />
            <Route path="/admin/kess3" element={<AdminKess3 />} />
            <Route path="/admin/tuning-data" element={<AdminTuningData />} />
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/account" element={<Account />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
          <ChatWidget />
          </LegacyRedirectGate>
          </OAuthReturnGate>
        </CartProvider>
      </BrowserRouter>
      <Toaster theme="light" position="bottom-left" toastOptions={{ style: { background: "#FFFFFF", border: "1px solid #E3E8EF", color: "#1D2939" } }} />
    </div>
  );
}

export default App;
